import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useAllTimeGreatsLeaderboard } from "../hooks/useAllTimeGreatsLeaderboard";
import { useGreatsGallery } from "../hooks/useGreatsGallery";
import { usePlayerHeadshots } from "../hooks/usePlayerHeadshots";
import {
  defaultStatForCategory,
  getAllTimeGreatsStat,
} from "../constants/allTimeGreats";
import {
  buildGreatsGalleryItems,
  filterLeaderboardRowsByName,
  getGreatsQualifier,
  parseAllTimeGreatsSearch,
  serializeAllTimeGreatsSearch,
  splitGreatsRows,
} from "../utils/allTimeGreats";
import { getHeadshotUrl } from "../utils/playerHeadshots";
import {
  buildLeaderboardRows,
  getLeaderboardStatFormat,
} from "../utils/leaderboardRows";
import { QUERY_PAGE_SIZE_OPTIONS } from "../utils/queryBuilder";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  formatGridStatValue,
  getResultsGridSx,
  sortedClassFor,
} from "../utils/resultsGrid";
import PlayerNameCell from "../components/PlayerNameCell";
import GreatsFeatured from "../components/allTimeGreats/GreatsFeatured";
import GreatsGallery from "../components/allTimeGreats/GreatsGallery";
import GreatsStatRail from "../components/allTimeGreats/GreatsStatRail";

const Leaderboards = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { category, stat } = parseAllTimeGreatsSearch(searchParams);
  const statConfig = getAllTimeGreatsStat(stat);
  const qualifier = getGreatsQualifier(stat);

  const [searchTerm, setSearchTerm] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState([
    { field: "statValue", sort: "desc" },
  ]);

  const { results, totalResults, isLoading, error } =
    useAllTimeGreatsLeaderboard({
      stat,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    });

  const sortedField = sortModel[0]?.field;
  const statFormat = getLeaderboardStatFormat(stat);

  const writeSearch = (next) => {
    setSearchParams(serializeAllTimeGreatsSearch(next), { replace: true });
    setPaginationModel((model) => ({ ...model, page: 0 }));
    setSearchTerm("");
    setSortModel([{ field: "statValue", sort: "desc" }]);
  };

  const rows = useMemo(
    () =>
      buildLeaderboardRows(results, stat, {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }),
    [paginationModel.page, paginationModel.pageSize, results, stat]
  );

  const playerIds = useMemo(
    () => rows.map((row) => row.playerId).filter(Boolean),
    [rows]
  );
  const { headshots } = usePlayerHeadshots(playerIds);

  const namedRows = useMemo(
    () => filterLeaderboardRowsByName(rows, searchTerm),
    [rows, searchTerm]
  );

  const visibleRows = useMemo(
    () =>
      namedRows.map((row) => ({
        ...row,
        displayValue: formatGridStatValue(row.statValue, statFormat),
        photoUrl: getHeadshotUrl(headshots, row.playerId),
      })),
    [headshots, namedRows, statFormat]
  );

  const { featured, list } = useMemo(
    () =>
      splitGreatsRows(visibleRows, {
        page: paginationModel.page,
        searchTerm,
      }),
    [paginationModel.page, searchTerm, visibleRows]
  );

  const galleryItems = useMemo(
    () => buildGreatsGalleryItems(featured, list),
    [featured, list]
  );
  const gallery = useGreatsGallery(galleryItems);

  const columns = useMemo(
    () => [
      {
        field: "rank",
        headerName: "#",
        width: 56,
        sortable: false,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "playerName",
        headerName: "Player",
        flex: 1.2,
        minWidth: 200,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <PlayerNameCell
            playerId={params.row.playerId}
            name={params.value}
            photoUrl={params.row.photoUrl}
            onPlayerClick={gallery.openAt}
          />
        ),
      },
      {
        field: "statValue",
        headerName: statConfig.label,
        width: 140,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("statValue", sortedField),
        cellClassName: sortedClassFor("statValue", sortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) =>
          params.row.displayValue ??
          formatGridStatValue(params.value, statFormat),
      },
    ],
    [gallery.openAt, sortedField, statConfig.label, statFormat]
  );

  return (
    <Container maxWidth="lg" sx={{ py: 1 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: "0.16em" }}
      >
        All-time greats
      </Typography>
      <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
        {statConfig.label}
      </Typography>
      {qualifier && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {qualifier}
        </Typography>
      )}

      <Box sx={{ mt: 3 }}>
        <GreatsStatRail
          category={category}
          stat={stat}
          onCategoryChange={(nextCategory) =>
            writeSearch({
              category: nextCategory,
              stat: defaultStatForCategory(nextCategory),
            })
          }
          onStatChange={(nextStat) =>
            writeSearch({ category, stat: nextStat })
          }
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load this leaderboard.
        </Alert>
      )}

      <GreatsFeatured
        players={featured}
        loading={isLoading && paginationModel.page === 0 && !searchTerm}
        onSelect={gallery.openAt}
      />

      <TextField
        label="Search this page"
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Player name"
        sx={{ mb: 2, maxWidth: 320 }}
      />

      <Paper sx={{ overflow: "hidden" }}>
        <DataGrid
          rows={list}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          rowCount={totalResults}
          disableRowSelectionOnClick
          disableColumnMenu
          sortingMode="client"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onRowClick={(params) => gallery.openAt(params.row.playerId)}
          autoHeight
          rowHeight={52}
          localeText={{
            noRowsLabel: searchTerm.trim()
              ? "No players on this page match."
              : "No players",
          }}
          sx={{
            ...getResultsGridSx("auto"),
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
        />
      </Paper>
      <GreatsGallery
        open={gallery.open}
        item={gallery.item}
        index={gallery.index}
        count={gallery.count}
        statLabel={statConfig.label}
        onClose={gallery.close}
        onStep={gallery.step}
      />
    </Container>
  );
};

export default Leaderboards;
